import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toTitleCase } from '../utils/dateUtils';
import { api } from '../services/api';
import { getPatientDetailsPath } from '../utils/patientNavigation';

const parseDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  const normalized = String(value).trim();
  if (!normalized) return null;

  const isoMatch = normalized.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?)?$/
  );
  if (isoMatch) {
    const [, year, month, day, hour = '0', minute = '0', second = '0'] = isoMatch;
    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    );
  }

  const [datePart = '', timePart = '00:00'] = normalized.split(' ');
  const dateSegments = datePart.split(/[\/\-.]/).map((segment) => segment.trim());
  if (dateSegments.length < 3) {
    const direct = new Date(normalized);
    return Number.isNaN(direct.getTime()) ? null : direct;
  }

  let day = Number(dateSegments[0]);
  let month = Number(dateSegments[1]);
  let year = Number(dateSegments[2]);
  if (dateSegments[0].length === 4) {
    year = Number(dateSegments[0]);
    month = Number(dateSegments[1]);
    day = Number(dateSegments[2]);
  }
  if ([day, month, year].some((segment) => Number.isNaN(segment))) return null;
  const [hour = 0, minute = 0] = timePart
    .split(':')
    .map((segment) => Number(segment) || 0);
  return new Date(year, month - 1, day, hour, minute);
};

const formatBirthdayLabel = (date) =>
  new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'long' }).format(date);

// Converte quantidades em formato pt-BR ("1.234,5") ou en ("1234.5") para número
const parseQuantity = (value) => {
  const raw = String(value ?? '').replace(/[\s ]/g, '');
  const normalized = raw.includes(',') ? raw.replace(/\./g, '').replace(',', '.') : raw;
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const formatQuantity = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '0';
  return Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 2 });
};

export default function Painel() {
  const { currentUser, patients } = useOutletContext();
  const navigate = useNavigate();
  const [selectedMeds, setSelectedMeds] = useState([]);
  const [showMedPicker, setShowMedPicker] = useState(false);
  const [overdueTransactions, setOverdueTransactions] = useState([]);
  const medPickerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (medPickerRef.current && !medPickerRef.current.contains(e.target)) {
        setShowMedPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    api.get('/financial?paymentStatus=pendente')
      .then(({ data }) => {
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const overdue = (Array.isArray(data) ? data : [])
          .filter((t) => {
            const d = new Date(t.date);
            d.setHours(0, 0, 0, 0);
            return d < now;
          })
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        setOverdueTransactions(overdue);
      })
      .catch(() => {});
  }, []);

  const safePatients = Array.isArray(patients) ? patients.filter((p) => p.status !== 'inactive') : [];
  const allPatients = Array.isArray(patients) ? patients : [];
  const msPerDay = 24 * 60 * 60 * 1000;
  const today = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  }, []);

  const upcomingBirthdays = useMemo(() => {
    return allPatients
      .map((patient) => {
        const birth = parseDateValue(patient.birthDate);
        if (!birth) return null;
        const birthdayThisYear = new Date(today.getFullYear(), birth.getMonth(), birth.getDate());
        const nextBirthday =
          birthdayThisYear < today
            ? new Date(today.getFullYear() + 1, birth.getMonth(), birth.getDate())
            : birthdayThisYear;
        const diffDays = Math.round((nextBirthday - today) / msPerDay);
        if (diffDays < 0 || diffDays > 15) return null;
        return {
          patient,
          nextBirthday,
          diffDays,
          age: nextBirthday.getFullYear() - birth.getFullYear(),
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.diffDays - b.diffDays);
  }, [allPatients, today, msPerDay]);

  const overduePatients = useMemo(() => {
    const list = [];
    safePatients.forEach((patient) => {
      const consultations = Array.isArray(patient.consultations) ? patient.consultations : [];
      let latest = null;
      consultations.forEach((consult) => {
        const parsed = parseDateValue(consult.date);
        if (parsed && (!latest || parsed > latest)) {
          latest = parsed;
        }
      });
      if (!latest && patient.lastVisit) {
        const fallback = parseDateValue(patient.lastVisit);
        if (fallback) latest = fallback;
      }
      if (latest && today - latest > 7 * msPerDay) {
        list.push({ patient, latest });
      }
    });
    return list;
  }, [safePatients, today, msPerDay]);

  const todaysBirthdays = useMemo(
    () => upcomingBirthdays.filter((item) => item.diffDays === 0),
    [upcomingBirthdays]
  );

  const monthStats = useMemo(() => {
    const now = new Date();
    const targetMonth = now.getMonth();
    const targetYear = now.getFullYear();
    let consultationsCount = 0;
    let medicationQuantity = 0;

    safePatients.forEach((patient) => {
      const consultations = Array.isArray(patient.consultations) ? patient.consultations : [];
      consultations.forEach((consult) => {
        const consultDate = parseDateValue(consult.date);
        if (!consultDate) return;
        if (
          consultDate.getMonth() === targetMonth &&
          consultDate.getFullYear() === targetYear
        ) {
          consultationsCount += 1;
          medicationQuantity += parseQuantity(consult.quantity ?? consult.appliedQuantity);
        }
      });
    });

    return { consultationsCount, medicationQuantity };
  }, [safePatients]);

  const allMedNames = useMemo(() => {
    const map = new Map();
    safePatients.forEach((patient) => {
      (patient.consultations || []).forEach((consult) => {
        const prods = Array.isArray(consult.products) && consult.products.length > 0
          ? consult.products
          : consult.productName ? [{ productName: consult.productName, laboratory: consult.laboratory }] : [];
        prods.forEach((p) => {
          if (p.productName && !map.has(p.productName)) {
            map.set(p.productName, p.laboratory || '');
          }
        });
      });
    });
    return [...map.entries()]
      .map(([name, laboratory]) => ({ name, laboratory }))
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
  }, [safePatients]);

  const last12MonthsData = useMemo(() => {
    const now = new Date();
    const months = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        year: d.getFullYear(), month: d.getMonth(),
        label: new Intl.DateTimeFormat('pt-BR', { month: 'short' }).format(d).replace('.', ''),
        consultas: 0, medicamentos: 0,
      });
    }

    safePatients.forEach((patient) => {
      (patient.consultations || []).forEach((consult) => {
        const date = parseDateValue(consult.date);
        if (!date) return;
        const entry = months.find((m) => m.year === date.getFullYear() && m.month === date.getMonth());
        if (!entry) return;
        entry.consultas += 1;

        const prods = Array.isArray(consult.products) && consult.products.length > 0
          ? consult.products
          : consult.productName ? [{ productName: consult.productName, quantity: consult.quantity ?? consult.appliedQuantity }] : [];

        prods.forEach((p) => {
          if (selectedMeds.length > 0 && !selectedMeds.includes(p.productName)) return;
          entry.medicamentos += parseQuantity(p.quantity);
        });
      });
    });

    return months;
  }, [safePatients, selectedMeds]);

  const daysLabel = (diff) => {
    if (diff === 0) return 'Hoje';
    if (diff === 1) return 'Amanhã';
    return `${diff} dias`;
  };

  // Pacientes cuja próxima consulta deve ter bioimpedância
  // (2 consultas após a última com anexo)
  const bioimpedanceDuePatients = useMemo(() => {
    return safePatients
      .map((patient) => {
        const consultations = Array.isArray(patient.consultations) ? patient.consultations : [];
        const sorted = [...consultations].sort((a, b) => new Date(a.date) - new Date(b.date));
        for (let i = sorted.length - 1; i >= 0; i--) {
          if (sorted[i].attachmentUrl) {
            const afterCount = sorted.length - 1 - i;
            if (afterCount === 2) {
              const lastConsultDate = parseDateValue(sorted[sorted.length - 1].date);
              return { patient, lastConsultDate };
            }
            break;
          }
        }
        return null;
      })
      .filter(Boolean)
      .sort((a, b) => (b.lastConsultDate || 0) - (a.lastConsultDate || 0));
  }, [safePatients]);

  const hour = new Date().getHours();
  const greetingWord = hour < 12 ? 'Bom dia' : hour < 18 ? 'Boa tarde' : 'Boa noite';
  const todayLabel = new Intl.DateTimeFormat('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date());
  const monthName = new Intl.DateTimeFormat('pt-BR', { month: 'long' }).format(new Date());

  const getInitials = (name) => {
    const parts = (name || '').trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const avatarTone = (name) => {
    const palettes = [
      { bg: '#FBE0E5', fg: '#9F4A60' },
      { bg: '#EFE3F0', fg: '#7A4A86' },
      { bg: '#FBEEDF', fg: '#A66730' },
      { bg: '#E5DCEC', fg: '#634A82' },
      { bg: '#F4DBDE', fg: '#A8556A' },
      { bg: '#E6F1EB', fg: '#3D8068' },
      { bg: '#E8E3DA', fg: '#65564A' },
    ];
    let h = 0;
    for (const c of (name || '')) h = (h * 31 + c.charCodeAt(0)) >>> 0;
    return palettes[h % palettes.length];
  };

  const getDayChip = (dias) => {
    if (dias >= 30) return { bg: '#FBE0E5', fg: '#9F4A60', label: `há ${dias} dias` };
    if (dias >= 14) return { bg: '#FBEEDF', fg: '#A66730', label: `há ${dias} dias` };
    return { bg: '#F2F2F7', fg: '#6E6E73', label: `há ${dias} dias` };
  };

  const formatCurrency = (value = 0) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value) || 0);

  const kpis = [
    {
      key: 'consultas',
      label: 'Consultas deste mês',
      value: String(monthStats.consultationsCount),
      subtitle: `Total de atendimentos lançados em ${monthName}`,
      bg: ['#FBE0E5', '#F4C4CE'],
      accent: '#9F4A60',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9F4A60" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="5" width="18" height="16" rx="2" /><path d="M8 3v4M16 3v4M3 11h18" />
        </svg>
      ),
    },
    {
      key: 'medicamentos',
      label: 'Medicamentos aplicados',
      value: formatQuantity(monthStats.medicationQuantity),
      subtitle: 'Quantidade acumulada nas consultas deste mês',
      bg: ['#EFE3F0', '#DECCDE'],
      accent: '#7A4A86',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7A4A86" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 4h6v3H9zM7 7h10l1 13H6z" /><path d="M10 12h4M12 10v4" />
        </svg>
      ),
    },
  ];

  return (
    <div className="patients-card" style={{ paddingTop: 44, paddingBottom: 56, color: '#1C1C1E', WebkitFontSmoothing: 'antialiased' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
        <div>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            background: '#FBE0E5', color: '#9F4A60',
            padding: '4px 10px', borderRadius: 999,
            fontSize: 11, letterSpacing: '-0.005em', fontWeight: 600,
            whiteSpace: 'nowrap', marginBottom: 12,
          }}>
            <span style={{ width: 5, height: 5, borderRadius: 999, background: '#C97A8F' }} />
            {todayLabel}
          </div>
          <h1 style={{ fontWeight: 700, fontSize: 38, lineHeight: 1.18, letterSpacing: '-0.035em', margin: 0, color: '#1C1C1E' }}>
            {greetingWord}{currentUser ? `, ${currentUser}` : ''}
          </h1>
          <div style={{ marginTop: 6, fontSize: 14, color: '#6E6E73', letterSpacing: '-0.005em' }}>
            Visão geral rápida da clínica
          </div>
        </div>
        <button
          onClick={() => navigate('/pacientes')}
          style={{
            fontFamily: 'inherit', fontSize: 13, fontWeight: 600, letterSpacing: '-0.005em',
            padding: '9px 14px', borderRadius: 10, border: '1px solid var(--button-color)',
            background: 'var(--button-color)', color: '#FFFFFF', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Ver pacientes
        </button>
      </div>

      {/* KPI grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        {kpis.map((k) => (
          <div key={k.key} style={{
            background: `linear-gradient(160deg, ${k.bg[0]} 0%, ${k.bg[1]} 100%)`,
            borderRadius: 22, padding: '14px 20px 16px', minHeight: 118,
            display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6), 0 1px 2px rgba(28,28,30,0.04), 0 14px 36px -22px rgba(28,28,30,0.22)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, letterSpacing: '-0.005em', color: 'rgba(28,28,30,0.7)' }}>
                {k.label}
              </div>
              <div style={{
                width: 36, height: 36, borderRadius: 12,
                background: 'rgba(255,255,255,0.55)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7)', flexShrink: 0,
              }}>
                {k.icon}
              </div>
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span style={{ fontWeight: 700, fontSize: 64, lineHeight: 0.95, letterSpacing: '-0.045em', color: k.accent, fontVariantNumeric: 'tabular-nums' }}>
                  {k.value}
                </span>
              </div>
              <div style={{ marginTop: 10, fontSize: 12.5, color: 'rgba(28,28,30,0.6)', letterSpacing: '-0.005em' }}>
                {k.subtitle}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Chart: consultas + medicamentos últimos 12 meses */}
      <div style={{ marginBottom: 16 }}>
        <PainelCard>
          <PainelCardHeader
            title="Histórico mensal"
            subtitle="Consultas realizadas e medicamentos aplicados nos últimos 12 meses"
          />
          <div style={{ padding: '20px 22px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
              <div style={{ display: 'flex', gap: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ width: 24, height: 3, borderRadius: 2, background: '#9F4A60', display: 'inline-block' }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#6E6E73', letterSpacing: '-0.005em' }}>Consultas</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ width: 24, height: 2, display: 'inline-block', borderBottom: '2px dashed #7A4A86' }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#6E6E73', letterSpacing: '-0.005em' }}>Medicamentos (mg)</span>
                </div>
              </div>

              {/* Med picker */}
              <div ref={medPickerRef} style={{ position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  {selectedMeds.length === 0 ? (
                    <span style={{ fontSize: 11.5, color: '#8E8E93', letterSpacing: '-0.005em' }}>Todos os medicamentos</span>
                  ) : (
                    selectedMeds.map((med) => (
                      <span key={med} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: '#7A4A86', background: '#EFE3F0', padding: '3px 8px', borderRadius: 999, letterSpacing: '-0.005em', whiteSpace: 'nowrap' }}>
                        {med}
                        <button onClick={() => setSelectedMeds((p) => p.filter((m) => m !== med))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#7A4A86', display: 'flex', alignItems: 'center', lineHeight: 1 }}>
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                        </button>
                      </span>
                    ))
                  )}
                  <button onClick={() => setShowMedPicker((v) => !v)} style={{ fontSize: 11, fontWeight: 600, color: '#9F4A60', background: '#FBE0E5', border: 'none', padding: '3px 10px', borderRadius: 999, cursor: 'pointer', whiteSpace: 'nowrap', letterSpacing: '-0.005em' }}>
                    {selectedMeds.length === 0 ? '+ Filtrar' : '+ Adicionar'}
                  </button>
                  {selectedMeds.length > 0 && (
                    <button onClick={() => setSelectedMeds([])} style={{ fontSize: 11, fontWeight: 600, color: '#8E8E93', background: 'none', border: 'none', cursor: 'pointer', padding: '3px 6px', letterSpacing: '-0.005em' }}>Limpar</button>
                  )}
                </div>

                {showMedPicker && (
                  <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 6, background: '#FFFFFF', border: '1px solid #E5E5EA', borderRadius: 14, boxShadow: '0 8px 24px rgba(28,28,30,0.12)', zIndex: 100, minWidth: 220, maxHeight: 240, overflowY: 'auto' }}>
                    {allMedNames.length === 0 ? (
                      <div style={{ padding: '12px 16px', fontSize: 13, color: '#8E8E93' }}>Nenhum medicamento encontrado</div>
                    ) : allMedNames.map(({ name, laboratory }) => {
                      const checked = selectedMeds.includes(name);
                      return (
                        <div
                          key={name}
                          onClick={() => setSelectedMeds((p) => checked ? p.filter((m) => m !== name) : [...p, name])}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px', cursor: 'pointer', background: checked ? '#EFE3F0' : 'transparent', borderBottom: '1px solid #F2F2F7', transition: 'background 0.12s' }}
                          onMouseEnter={(e) => { if (!checked) e.currentTarget.style.background = '#FAFAFB'; }}
                          onMouseLeave={(e) => { if (!checked) e.currentTarget.style.background = 'transparent'; }}
                        >
                          <div style={{ width: 16, height: 16, borderRadius: 5, border: `2px solid ${checked ? '#7A4A86' : '#D1D1D6'}`, background: checked ? '#7A4A86' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.12s' }}>
                            {checked && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 13, color: '#1C1C1E', fontWeight: checked ? 600 : 400, letterSpacing: '-0.005em', lineHeight: 1.3 }}>{name}</div>
                            {laboratory && <div style={{ fontSize: 11, color: '#8E8E93', marginTop: 1, letterSpacing: '-0.005em' }}>{laboratory}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={last12MonthsData} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid stroke="#F2F2F7" strokeDasharray="0" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fontWeight: 600, fill: '#8E8E93', fontFamily: 'inherit' }}
                  axisLine={false}
                  tickLine={false}
                  dy={8}
                />
                <YAxis
                  yAxisId="consultas"
                  orientation="left"
                  tick={{ fontSize: 11, fill: '#8E8E93', fontFamily: 'inherit' }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                  width={32}
                />
                <YAxis
                  yAxisId="medicamentos"
                  orientation="right"
                  tick={{ fontSize: 11, fill: '#8E8E93', fontFamily: 'inherit' }}
                  axisLine={false}
                  tickLine={false}
                  width={38}
                />
                <Tooltip
                  contentStyle={{
                    fontFamily: 'inherit', fontSize: 12, fontWeight: 500,
                    border: '1px solid #E5E5EA', borderRadius: 12,
                    boxShadow: '0 8px 24px rgba(28,28,30,0.10)',
                    padding: '10px 14px', color: '#1C1C1E',
                  }}
                  itemStyle={{ fontSize: 12 }}
                  labelStyle={{ fontWeight: 700, marginBottom: 4, color: '#1C1C1E' }}
                  formatter={(value, name) => [
                    name === 'consultas' ? value : `${value.toLocaleString('pt-BR')} mg`,
                    name === 'consultas' ? 'Consultas' : 'Medicamentos',
                  ]}
                />
                <Line
                  yAxisId="consultas"
                  type="monotone"
                  dataKey="consultas"
                  stroke="#9F4A60"
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: '#9F4A60', strokeWidth: 0 }}
                  activeDot={{ r: 6, fill: '#9F4A60', strokeWidth: 0 }}
                />
                <Line
                  yAxisId="medicamentos"
                  type="monotone"
                  dataKey="medicamentos"
                  stroke="#7A4A86"
                  strokeWidth={2.5}
                  strokeDasharray="5 3"
                  dot={{ r: 4, fill: '#7A4A86', strokeWidth: 0 }}
                  activeDot={{ r: 6, fill: '#7A4A86', strokeWidth: 0 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </PainelCard>
      </div>

      {/* Two-column section */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16 }}>

        {/* Left: Sem consulta há 7+ dias */}
        <PainelCard>
          <PainelCardHeader
            title="Sem consulta há 7+ dias"
            subtitle={`${overduePatients.length} paciente${overduePatients.length !== 1 ? 's' : ''} precisam de follow-up`}
            badge={overduePatients.length > 0 ? { bg: '#FBEEDF', fg: '#A66730', label: String(overduePatients.length) } : null}
          />
          {overduePatients.length === 0 ? (
            <PainelEmpty
              icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#A66730" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>}
              label="Todos em dia"
              hint="Nenhum paciente está há mais de 7 dias sem consulta."
            />
          ) : (
            <div style={{ padding: '4px 0' }}>
              {overduePatients.map(({ patient, latest }, i) => {
                const dias = Math.floor((today - latest) / msPerDay);
                const tone = avatarTone(patient.name);
                const chip = getDayChip(dias);
                return (
                  <div
                    key={patient._id || patient.id}
                    onClick={() => navigate(getPatientDetailsPath(patient?._id), { state: patient })}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '10px 22px', cursor: 'pointer',
                      borderTop: i === 0 ? 'none' : '1px solid #F2F2F7',
                    }}
                  >
                    <div style={{
                      width: 34, height: 34, borderRadius: 999,
                      background: tone.bg, color: tone.fg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 700, letterSpacing: '-0.01em', flexShrink: 0,
                    }}>
                      {getInitials(patient.name)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: '#1C1C1E', letterSpacing: '-0.005em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {toTitleCase(patient.name)}
                      </div>
                      <div style={{ fontSize: 12, color: '#8E8E93', marginTop: 1, letterSpacing: '-0.005em' }}>
                        Última consulta: {latest ? new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(latest) : '—'}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: chip.fg, background: chip.bg, padding: '4px 10px', borderRadius: 999, whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {chip.label}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </PainelCard>

        {/* Right: stacked */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Pagamentos atrasados */}
          {(() => {
            const totalAtraso = overdueTransactions.reduce((s, t) => s + t.amount, 0);
            return (
              <PainelCard>
                <PainelCardHeader
                  title="Pagamentos atrasados"
                  subtitle={overdueTransactions.length > 0 ? `Total vencido: ${formatCurrency(totalAtraso)}` : 'Nenhum pagamento em atraso'}
                  badge={overdueTransactions.length > 0 ? { bg: '#FBE0E5', fg: '#9F4A60', label: String(overdueTransactions.length) } : null}
                />
                {overdueTransactions.length === 0 ? (
                  <PainelEmpty
                    icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9F4A60" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>}
                    label="Tudo em dia"
                    hint="Nenhum pagamento com vencimento em atraso."
                  />
                ) : (
                  <>
                    <div style={{ padding: '4px 0' }}>
                      {overdueTransactions.slice(0, 5).map((t, i) => {
                        const pat = allPatients.find((p) => String(p._id) === String(t.patientId));
                        const patientName = pat?.name || t.description || 'Desconhecido';
                        const dueDate = new Date(t.date);
                        dueDate.setHours(0, 0, 0, 0);
                        const diasAtraso = Math.floor((today - dueDate) / msPerDay);
                        const tone = avatarTone(patientName);
                        return (
                          <div
                            key={t._id || i}
                            onClick={() => navigate('/financeiro', { state: { filterStatus: 'atrasado' } })}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 12,
                              padding: '10px 22px', cursor: 'pointer',
                              borderTop: i === 0 ? 'none' : '1px solid #F2F2F7',
                            }}
                          >
                            <div style={{
                              width: 34, height: 34, borderRadius: 999,
                              background: tone.bg, color: tone.fg,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 12, fontWeight: 700, letterSpacing: '-0.01em', flexShrink: 0,
                            }}>
                              {getInitials(patientName)}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 14, fontWeight: 500, color: '#1C1C1E', letterSpacing: '-0.005em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {toTitleCase(patientName)}
                              </div>
                              <div style={{ fontSize: 12, color: '#8E8E93', marginTop: 1, display: 'flex', alignItems: 'center', gap: 6, letterSpacing: '-0.005em', fontVariantNumeric: 'tabular-nums', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.description || t.category}</span>
                                <span style={{ width: 3, height: 3, borderRadius: 999, background: '#D1D1D6', flexShrink: 0 }} />
                                <span style={{ flexShrink: 0 }}>venc. {new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(dueDate)}</span>
                              </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                              <div style={{ fontSize: 14, fontWeight: 700, color: '#9F4A60', letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                {formatCurrency(t.amount)}
                              </div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: '#9F4A60', background: '#FBE0E5', padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                                {diasAtraso} dias em atraso
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ padding: '12px 22px 16px', borderTop: '1px solid #F2F2F7' }}>
                      <button
                        onClick={() => navigate('/financeiro', { state: { filterStatus: 'atrasado' } })}
                        style={{
                          fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600,
                          color: '#9F4A60', background: '#FBE0E5',
                          border: 'none', borderRadius: 10, cursor: 'pointer',
                          padding: '9px 14px', letterSpacing: '-0.005em',
                          width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        }}
                      >
                        Ver todas as cobranças
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M5 12h14M13 6l6 6-6 6" />
                        </svg>
                      </button>
                    </div>
                  </>
                )}
              </PainelCard>
            );
          })()}

          {/* Próximos aniversários */}
          <PainelCard>
            <PainelCardHeader title="Próximos aniversários" subtitle="Próximos 15 dias" />
            {upcomingBirthdays.length === 0 ? (
              <PainelEmpty
                icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9F4A60" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5c-1 0-2-1-2-2s1-2 2-2 2 1 2 2-1 2-2 2zM6 14c0-3 2-5 6-5s6 2 6 5v3H6zM4 17h16M4 21h16"/></svg>}
                label="Nenhum aniversário previsto"
                hint="Avisos aparecem aqui automaticamente nos próximos 15 dias."
              />
            ) : (
              <div style={{ padding: '4px 0' }}>
                {upcomingBirthdays.map(({ patient, nextBirthday, diffDays, age }, i) => {
                  const tone = avatarTone(patient.name);
                  const isToday = diffDays === 0;
                  return (
                    <div
                      key={patient._id || patient.id}
                      onClick={() => navigate(getPatientDetailsPath(patient?._id), { state: patient })}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '10px 22px', cursor: 'pointer',
                        borderTop: i === 0 ? 'none' : '1px solid #F2F2F7',
                      }}
                    >
                      <div style={{
                        width: 34, height: 34, borderRadius: 999,
                        background: tone.bg, color: tone.fg,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700, flexShrink: 0,
                      }}>
                        {getInitials(patient.name)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 500, color: '#1C1C1E', letterSpacing: '-0.005em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {toTitleCase(patient.name)}
                        </div>
                        <div style={{ fontSize: 12, color: '#8E8E93', marginTop: 1 }}>
                          {formatBirthdayLabel(nextBirthday)} · {age} anos
                        </div>
                      </div>
                      <div style={{
                        fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 999, whiteSpace: 'nowrap', flexShrink: 0,
                        background: isToday ? '#FBE0E5' : '#F2F2F7',
                        color: isToday ? '#9F4A60' : '#6E6E73',
                      }}>
                        {isToday ? '🎂 Hoje' : daysLabel(diffDays)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </PainelCard>

          {/* Próximas bioimpedâncias */}
          <PainelCard>
            <PainelCardHeader
              title="Próximas bioimpedâncias"
              subtitle="Pacientes no ciclo de nova medição"
              badge={bioimpedanceDuePatients.length > 0 ? { bg: '#EFE3F0', fg: '#7A4A86', label: String(bioimpedanceDuePatients.length) } : null}
            />
            {bioimpedanceDuePatients.length === 0 ? (
              <PainelEmpty
                icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#7A4A86" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4 21h16M6 21l2-10h8l2 10M12 4v3M9 4h6M9 11a3 3 0 006 0"/></svg>}
                label="Nada agendado no momento"
                hint="Quando um paciente estiver no ciclo de nova bioimpedância, ele aparece aqui."
              />
            ) : (
              <div style={{ padding: '4px 0' }}>
                {bioimpedanceDuePatients.map(({ patient, lastConsultDate }, i) => {
                  const tone = avatarTone(patient.name);
                  return (
                    <div
                      key={patient._id || patient.id}
                      onClick={() => navigate(getPatientDetailsPath(patient?._id), { state: patient })}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '10px 22px', cursor: 'pointer',
                        borderTop: i === 0 ? 'none' : '1px solid #F2F2F7',
                      }}
                    >
                      <div style={{
                        width: 34, height: 34, borderRadius: 999,
                        background: tone.bg, color: tone.fg,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700, flexShrink: 0,
                      }}>
                        {getInitials(patient.name)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 500, color: '#1C1C1E', letterSpacing: '-0.005em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {toTitleCase(patient.name)}
                        </div>
                        <div style={{ fontSize: 12, color: '#8E8E93', marginTop: 1 }}>
                          Última consulta: {lastConsultDate ? new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(lastConsultDate) : '—'}
                        </div>
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#7A4A86', background: '#EFE3F0', padding: '4px 10px', borderRadius: 999, whiteSpace: 'nowrap', flexShrink: 0 }}>
                        Medir →
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </PainelCard>

        </div>
      </div>
    </div>
  );
}

const PainelCard = ({ children, style }) => (
  <div style={{
    background: '#FFFFFF', border: '1px solid #E5E5EA', borderRadius: 22, overflow: 'hidden',
    boxShadow: '0 1px 2px rgba(28,28,30,0.04), 0 14px 36px -22px rgba(28,28,30,0.12)',
    ...style,
  }}>{children}</div>
);

const PainelCardHeader = ({ title, subtitle, badge }) => (
  <div style={{
    padding: '18px 22px 14px', borderBottom: '1px solid #F2F2F7',
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
  }}>
    <div style={{ minWidth: 0, flex: 1 }}>
      <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.02em', color: '#1C1C1E' }}>{title}</div>
      {subtitle && <div style={{ marginTop: 3, fontSize: 12.5, color: '#6E6E73', letterSpacing: '-0.005em' }}>{subtitle}</div>}
    </div>
    {badge && (
      <div style={{ fontSize: 12, fontWeight: 700, color: badge.fg, background: badge.bg, padding: '4px 10px', borderRadius: 999, whiteSpace: 'nowrap' }}>
        {badge.label}
      </div>
    )}
  </div>
);

const PainelEmpty = ({ icon, label, hint }) => (
  <div style={{ padding: '30px 22px 32px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 10 }}>
    <div style={{ width: 44, height: 44, borderRadius: 14, background: '#F5F5F7', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #E5E5EA' }}>
      {icon}
    </div>
    <div style={{ fontSize: 14, fontWeight: 600, color: '#1C1C1E', letterSpacing: '-0.005em' }}>{label}</div>
    <div style={{ fontSize: 12, color: '#8E8E93', maxWidth: 300, lineHeight: 1.4, letterSpacing: '-0.005em' }}>{hint}</div>
  </div>
);
